<template>
  <div v-if="selectedImage" class="container image-gallery">
    <div
      class="image-gallery-list"
      data-track-as="imagegallery"
      data-track-text="open"
    >
      <div class="image-container">
        <img
          :src="selectedImage.imgSrc"
          :alt="selectedImage.title"
          :srcset="selectedImage.imgSrcset"
        />
        <dotcom-icon
          name="expand"
          art="regular"
          data-track-text="expand"
          class="image-expand-icon"
          color="--c-primary-neutral-3"
          hover-color="--c-primary-neutral-3"
          @click="expandImage"
        />
        <div class="image-counter">
          {{ `${currentIndex + 1} / ${localImages.length}` }}
        </div>
        <div class="image-action-buttons">
          <a v-show="!firstItem" class="prev" data-track-text="prev" nohref>
            <dotcom-icon
              name="chevron-right"
              art="regular"
              color="--c-primary-neutral-3"
              hover-color="--c-primary-neutral-3"
              rotation="180"
              @click="prevImage"
            />
          </a>
          <a v-show="!lastItem" class="next" data-track-text="next" nohref>
            <dotcom-icon
              name="chevron-right"
              art="regular"
              color="--c-primary-neutral-3"
              hover-color="--c-primary-neutral-3"
              @click="nextImage"
            />
          </a>
        </div>
      </div>
      <div class="image-gallery-content">
        <div class="description">
          <div class="description-caption">{{ selectedImage.title }}</div>
          <div class="description-content">{{ selectedImage.description }}</div>
          <div class="description-author">{{ selectedImage.author }}</div>
        </div>
      </div>
    </div>
    <div
      v-if="overlayActive"
      class="image-gallery-overlay"
      @click.self="closeOverlay"
    >
      <a class="close" data-track-text="close" nohref @click="closeOverlay">
        <dotcom-icon
          name="xmark"
          art="light"
          color="--c-primary-neutral-3"
          hover-color="--c-primary-neutral-3"
        />
      </a>
      <div class="overlay-content">
        <div class="image">
          <div class="image-counter">
            {{ `${currentIndex + 1} / ${localImages.length}` }}
          </div>
          <div class="image-container">
            <a class="prev" data-track-text="prev" nohref>
              <dotcom-icon
                v-show="!firstItem"
                name="chevron-right"
                art="regular"
                color="--c-primary-neutral-3"
                hover-color="--c-primary-neutral-3"
                rotation="180"
                @click="prevImage"
              />
            </a>
            <div class="image-wrapper">
              <img
                ref="imgRef"
                :src="selectedImage.imgSrc"
                :alt="selectedImage.title"
                :srcset="selectedImage.imgSrcset"
              />
            </div>
            <a class="next" data-track-text="next" nohref>
              <dotcom-icon
                v-show="!lastItem"
                name="chevron-right"
                art="regular"
                color="--c-primary-neutral-3"
                hover-color="--c-primary-neutral-3"
                @click="nextImage"
              />
            </a>
          </div>
          <div class="image-description">
            <div class="image-description-caption">
              {{ selectedImage.title }}
            </div>
            <div class="image-description-subtext">
              {{ selectedImage.description }}
            </div>
            <div class="image-description-author">
              {{ selectedImage.author }}
            </div>
          </div>
        </div>
        <div class="gallery">
          <div ref="overlayImages" class="gallery-images">
            <template v-for="(image, i) in localImages">
              <div
                :key="i"
                :ref="'image' + i"
                class="gallery-images-image"
                @click="selectImage(i)"
              >
                <div v-if="image !== selectedImage" class="white-overlay"></div>
                <img
                  :src="image.imgSrc"
                  :alt="image.title"
                  height="100px"
                  width="100px"
                  :srcset="image.imgSrcset"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SanitasMagazineImageGallery',

  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },

  props: {
    images: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      currentIndex: null,
      scrollingIndex: null,
      overlayActive: false,
      selectedImage: null,
      localImages: [],
    }
  },

  computed: {
    firstItem() {
      return this.currentIndex === 0
    },
    lastItem() {
      return this.currentIndex === this.localImages.length - 1
    },
  },

  watch: {
    overlayActive(value) {
      document.body.style.overflow = value ? 'hidden' : 'auto'
      if (value) {
        this.currentIndex = 0
      }
    },
  },

  mounted() {
    const self = this
    window.addEventListener('keydown', e => {
      self.handleGlobalKeyDown(e)
    })

    if (this.images) {
      this.localImages = JSON.parse(this.images)
      this.selectedImage = this.localImages[0]
      this.currentIndex = 0
      this.scrollingIndex = 0
    }
  },

  methods: {
    selectImage(i) {
      this.currentIndex = i
      this.selectedImage = this.localImages[this.currentIndex]
    },
    scroll() {
      this.$refs[`image${this.currentIndex}`][0].scrollIntoView({
        behavior: 'smooth',
      })
    },
    expandImage() {
      this.overlayActive = true
    },
    nextImage() {
      if (this.currentIndex === this.localImages.length - 1) {
        this.selectedImage = this.localImages[this.currentIndex]
      } else {
        this.selectedImage = this.localImages[++this.currentIndex]
      }
      this.scroll()
    },
    prevImage() {
      if (this.currentIndex === 0) {
        this.selectedImage = this.localImages[this.currentIndex]
      } else {
        this.selectedImage = this.localImages[--this.currentIndex]
      }
      this.scroll()
    },
    closeOverlay() {
      this.overlayActive = false
    },
    handleGlobalKeyDown(e) {
      switch (e.keyCode) {
        case 37:
          this.prevImage()
          break
        case 39:
          this.nextImage()
          break
        case 27:
          this.closeOverlay()
          break
        default:
          break
      }
    },
  },
}
</script>

<style lang="postcss">
.image-gallery {
  a:hover {
    border-bottom: none;
  }

  &-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    color: white;
    margin-bottom: 40px;

    .image {
      &-expand-icon {
        position: absolute;
        top: 20px;
        right: 20px;
        cursor: pointer;
        font-size: 26px;
      }

      &-counter {
        position: absolute;
        top: 20px;
        left: 20px;
        font-size: 15px;
        color: var(--c-primary-neutral-3);
        font-weight: 700;
      }

      &-action-buttons {
        position: absolute;
        top: calc(50% - 36px);
        overflow-x: hidden;
        left: 0;
        right: 0;
        display: flex;
        justify-content: space-between;

        .prev,
        .next {
          overflow: hidden;
        }

        .prev {
          margin-left: var(--spacing-2);
          margin-right: auto;
        }

        .next {
          margin-right: var(--spacing-2);
          margin-left: auto;
        }
      }

      &-container {
        width: 100%;
        max-height: 479px;
        position: relative;

        img {
          width: 100%;
          height: 100%;
        }
      }
    }
  }

  &-content {
    width: 100%;
    display: flex;
    background-color: var(--c-primary-neutral-3);
    cursor: default;
    color: var(--c-primary-neutral-1);
    border: 1px solid var(--c-secondary-neutral-2);
    border-top: none;

    .description {
      padding: 20px;
      width: 100%;
      line-height: 20px;
      font-size: 15px;

      &-caption {
        font-weight: bold;
      }

      &-author,
      &-content {
        word-break: break-word;
      }

      &-author {
        text-align: left;
        margin-top: 15px;
        font-size: 12px;
      }

      &-icon {
        cursor: pointer;
        margin: -12px 0 auto auto;

        a {
          border-bottom: none;
        }
      }
    }
  }

  &-overlay {
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    background-color: var(--c-primary-neutral-1);
    padding: 50px;
    z-index: calc(var(--z-index-overlay) - 1);
    display: flex;
    flex-direction: column;

    .close {
      position: absolute;
      right: 26px;
      top: 26px;
      cursor: pointer;
      text-decoration: none;
      z-index: 1;

      &:hover,
      &:visited,
      &:link,
      &:active {
        text-decoration: none;
        border-bottom: none;
      }
    }

    .overlay {
      &-content {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;

        .image {
          display: flex;
          flex-direction: column;
          margin: 0;
          max-height: 100%;
          height: calc(100% - 123px);

          &-counter {
            position: absolute;
            margin-bottom: 20px;
            font-size: 15px;
            font-weight: normal;
            font-style: normal;
            line-height: 1.33;
            color: var(--c-primary-neutral-3);
          }

          &-container {
            flex: 1;
            position: relative;
            display: flex;

            .image-wrapper {
              flex: 1;
              position: relative;

              img {
                position: absolute;
                left: 0;
                top: 0;
                height: 100%;
                width: 100%;
                object-fit: contain;
              }
            }

            .next,
            .prev {
              display: flex;
              overflow: hidden;
              width: 32px;

              .dotcom-icon {
                margin: auto 0;
              }
            }

            .next {
              padding-left: 10px;
            }

            .prev {
              padding-right: 10px;
            }
          }

          &-description {
            color: var(--c-primary-neutral-3);
            margin: 15px auto auto auto;
            max-width: 900px;
            width: 100%;
            display: flex;
            flex-direction: column;
            text-align: center;

            &-caption {
              font-size: 15px;
              font-weight: bold;
              line-height: 20px;
            }

            &-subtext,
            &-author {
              word-break: break-word;
              font-size: 15px;
              line-height: 20px;
            }
          }
        }

        .gallery {
          padding-top: 20px;
          width: 100%;
          display: flex;
          overflow: hidden;

          &-images {
            margin: auto;
            display: flex;
            overflow: hidden;

            &-image {
              position: relative;
              scroll-padding-left: 20px;
              scroll-padding-right: 20px;
              margin-left: 10px;
              margin-right: 10px;
              cursor: pointer;

              img {
                object-fit: cover;
              }

              .white-overlay {
                position: absolute;
                background-color: var(--c-secondary-neutral-2);
                top: 0;
                left: 0;
                bottom: 2px;
                width: 100%;
              }

              &:hover .white-overlay {
                display: none;
              }
            }
          }

          .prev,
          .next {
            display: flex;

            .dotcom-icon {
              margin: auto -29px;
            }
          }

          .prev {
            margin-left: auto;
            margin-right: 10px;
          }

          .next {
            margin-right: auto;
            margin-left: 10px;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .image-gallery-list .image-container .image-expand-icon {
    display: none;
  }
}

@media (max-width: 490px) {
  .image-gallery {
    &-overlay {
      .holder {
        margin: 20% 0;
      }
    }

    &-list {
      .image-container {
        max-height: 192px;

        .image-counter {
          left: 10px;
          top: 10px;
        }

        .image-action-buttons {
          .prev {
            margin-left: -18px;
          }

          .next {
            margin-right: -18px;
          }
        }
      }
    }

    &-content {
      display: block;
      position: relative;

      .description {
        padding: 10px;

        &-icon {
          display: none;
        }
      }
    }
  }
}
</style>

<style scoped>
.image-action-buttons,
.overlay-content {
  & >>> .dotcom-icon {
    font-size: 30px;
  }
}

.close >>> .dotcom-icon {
  font-size: 45px;
}
</style>
